// Puertos (§2 bis, SOLID: I y D). El núcleo depende de estas interfaces; cada
// plataforma provee adaptadores. Toda implementación pasa la suite de contrato
// de `/testing` (L: sustitución demostrada, no declarada).

import type {
  DoorbellVersions,
  OpGroup,
  OpResult,
  PullRequest,
  PullResult,
  PushResult,
  RecoveryItem,
  RecoveryKind,
  Row,
  ScopeState,
} from "./types";

/** Página que se escribe de una vez (una transacción). */
export interface ReplicaPage {
  upserts: Row[];
  deletes: string[];
  /** Foto inicial terminada: reemplaza el alcance entero por `upserts`. */
  replaceAll?: boolean;
  state: ScopeState;
}

/** Filas confirmadas por alcance. Solo guarda lo que viene del servidor. */
export interface ReplicaStore {
  loadScope(scope: string): Promise<{ state: ScopeState | null; rows: Row[] }>;
  applyPage(scope: string, page: ReplicaPage): Promise<void>;
  saveState(state: ScopeState): Promise<void>;
  clearScope(scope: string): Promise<void>;
  listStates(): Promise<ScopeState[]>;
  clearAll(): Promise<void>;
}

/** Operaciones pendientes y bandejas. Debe ser durable: es trabajo del usuario. */
export interface OutboxStore {
  list(): Promise<OpGroup[]>;
  put(group: OpGroup): Promise<void>;
  remove(groupId: string): Promise<void>;
  listRecovery(): Promise<RecoveryItem[]>;
  putRecovery(item: RecoveryItem): Promise<void>;
  removeRecovery(groupId: string): Promise<void>;
  clearAll(): Promise<void>;
}

/** Errores de red o del servidor que conviene reintentar. */
export class TransientError extends Error {
  constructor(
    message: string,
    /** `network`: sin conexión (no cuenta para el freno). `server`: error del servidor. */
    readonly kind: "network" | "server" = "network",
  ) {
    super(message);
    this.name = "TransientError";
  }
}

export interface SyncTransport {
  /** Lanza `TransientError` ante fallas de red o del servidor. */
  pull(request: PullRequest): Promise<PullResult>;
  push(group: OpGroup, protocolVersion: number): Promise<PushResult>;
  /** Timbre: versión por alcance. Devuelve la función para dejar de escuchar. */
  subscribe(scopes: string[], onVersions: (versions: DoorbellVersions) => void): () => void;
  isOnline(): boolean;
  onOnlineChange(listener: (online: boolean) => void): () => void;
}

/** Una sola pestaña sincroniza; las demás escriben en la outbox y escuchan. */
export interface LeaderElection {
  isLeader(): boolean;
  onLeaderChange(listener: (leader: boolean) => void): () => void;
  /** Avisa a las demás pestañas que algo cambió en los stores. */
  broadcast(message: LeaderMessage): void;
  onMessage(listener: (message: LeaderMessage) => void): () => void;
  dispose(): void;
}

export type LeaderMessage =
  | { type: "outbox" }
  | { type: "replica"; scope: string }
  | { type: "recovery" }
  /** La líder envió un grupo: la pestaña que lo hizo resuelve su `call.server`. */
  | { type: "pushed"; groupId: string; results: OpResult[] }
  | { type: "rejected"; groupId: string; kind: RecoveryKind; code: string; message: string };

export interface Clock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const systemClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** Sin otras pestañas (pruebas, Node, móvil): siempre líder. */
export const soloLeader: LeaderElection = {
  isLeader: () => true,
  onLeaderChange: () => () => {},
  broadcast: () => {},
  onMessage: () => () => {},
  dispose: () => {},
};
